// src/pages/EmployeesPage.js
// จัดการพนักงาน (Employees)
// UI เข้าธีมโมเดิร์นขาว-เทา-ชมพูอ่อน + ฟอร์มเพิ่มพนักงานใหม่จัดวางใหม่ให้เหมาะสม

import React, { useContext, useEffect, useState } from "react";
import api from "../api";
import AuthContext from "../AuthContext";
import { useNotification } from "../NotificationContext";
import SkeletonLoader from "../components/SkeletonLoader";

const EmployeesPage = () => {
  const { user } = useContext(AuthContext);
  const { addNotification } = useNotification();
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({
    emp_name: "",
    base_salary: "",
    username: "",
    role: "employee",
  });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  const isAdmin = user?.role === "admin";

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const res = await api.get("/employees");
      setEmployees(res.data || []);
    } catch (err) {
      addNotification(
        err.response?.data?.message || "โหลดรายชื่อพนักงานไม่สำเร็จ",
        "danger"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
    if (!isAdmin) {
      addNotification(
        "คุณไม่ได้เป็นผู้ดูแลระบบ (admin) — สามารถดูรายชื่อเท่านั้น",
        "warning"
      );
    }
  }, [isAdmin, addNotification]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((p) => ({ ...p, [name]: value }));
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    try {
      setSaving(true);
      await api.post("/employees", {
        emp_name: form.emp_name,
        base_salary: form.base_salary || null,
        username: form.username || null,
        role: form.role || "employee",
      });
      setForm({ emp_name: "", base_salary: "", username: "", role: "employee" });
      addNotification("เพิ่มพนักงานสำเร็จ", "success");
      setShowAddModal(false);
      await loadEmployees();
    } catch (err) {
      addNotification(
        err.response?.data?.message || "เพิ่มพนักงานไม่สำเร็จ",
        "danger"
      );
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (emp) => {
    setEditId(emp.emp_id);
    setEditForm({
      emp_name: emp.emp_name || "",
      base_salary: emp.base_salary ?? "",
      username: emp.login_username || "",
      role: emp.login_role || "employee",
    });
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    if (!isAdmin || !editId) return;
    try {
      setSaving(true);
      console.log("Saving employee:", { ...editForm, role: editForm.role }); // Debug log
      await api.put(`/employees/${editId}`, {
        emp_name: editForm.emp_name,
        base_salary: editForm.base_salary || null,
        username: editForm.username,
        role: editForm.role || "employee",
      });
      setEditId(null);
      setEditForm({});
      addNotification("บันทึกข้อมูลพนักงานสำเร็จ", "success");
      await loadEmployees();
    } catch (err) {
      addNotification(
        err.response?.data?.message || "บันทึกข้อมูลพนักงานไม่สำเร็จ",
        "danger"
      );
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async (emp) => {
    if (!isAdmin || !emp.login_username) return;
    const pw = window.prompt(
      `กำหนดรหัสผ่านใหม่ให้ "${emp.emp_name}" (ปล่อยว่าง = 123456):`
    );
    if (pw === null) return;
    try {
      setSaving(true);
      await api.post(`/employees/${emp.emp_id}/reset-password`, {
        new_password: pw || null,
      });
      addNotification(
        `ตั้งรหัสผ่านใหม่ให้ ${emp.emp_name} เรียบร้อยแล้ว\nusername: ${emp.login_username
        }\npassword: ${pw || "123456"}`,
        "success"
      );
    } catch (err) {
      addNotification(
        err.response?.data?.message || "รีเซ็ตรหัสผ่านพนักงานไม่สำเร็จ",
        "danger"
      );
    } finally {
      setSaving(false);
    }
  };

  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [permissions, setPermissions] = useState([]);

  const ALL_PERMISSIONS = [
    // Core features
    { id: "pos", label: "POS (ขายหน้าร้าน)", group: "core", isCore: true, icon: "bi-cart-check" },
    { id: "services", label: "Services (คลังบริการ/สินค้า)", group: "core", isCore: true, icon: "bi-box-seam" },
    { id: "appointments", label: "Appointments (นัดหมาย)", group: "core", isCore: true, icon: "bi-calendar-check" },
    { id: "customers", label: "VIP Members (ลูกค้า & มัดจำ)", group: "core", isCore: true, icon: "bi-people-fill" },
    { id: "barcode", label: "Barcode Printing (พิมพ์บาร์โค้ด)", group: "core", isCore: true, icon: "bi-upc-scan" },
    // Management & Finance
    {
      id: "cashflow",
      label: "Cashflow (จัดการเงิน)",
      group: "management",
      icon: "bi-arrow-left-right",
      badge: "สด/โอน ย้อนหลัง 7 วัน",
      desc: "พนักงานดูสรุปรายรับ-จ่าย สด/โอน และความเคลื่อนไหวย้อนหลังได้สูงสุด 7 วัน"
    },
    { id: "expenses", label: "Expenses (รายจ่ายร้าน)", group: "management", icon: "bi-wallet2" },
    { id: "commissions", label: "Commissions (คอมมิชชั่น)", group: "management", icon: "bi-percent" },
    { id: "deductions", label: "Deductions (หักเงิน)", group: "management", icon: "bi-dash-circle" },
    { id: "profit", label: "Net Profit (กำไรสุทธิ)", group: "management", icon: "bi-graph-up" },
    { id: "dashboard", label: "Dashboard (ภาพรวม)", group: "management", icon: "bi-speedometer2" },
    { id: "discount_codes", label: "Discount Codes (โค้ดส่วนลด)", group: "management", icon: "bi-tag" },
    { id: "reports", label: "Reports (รายงานเงินเดือน)", group: "management", icon: "bi-file-earmark-text" },
  ];

  const ROLE_DEFAULTS = {
    employee: ["pos", "services", "appointments", "customers", "barcode"],
    manager: ALL_PERMISSIONS.map(p => p.id),
    admin: [], // Admin has full access regardless
  };

  const openPermissionsModal = (emp) => {
    setSelectedEmp(emp);
    // If permissions is empty/null, use Role Defaults. Otherwise use existing.
    const initialPerms = (emp.permissions && emp.permissions.length > 0)
      ? emp.permissions
      : (ROLE_DEFAULTS[emp.login_role] || []);
    setPermissions(initialPerms);
    setShowPermissionsModal(true);
  };

  const togglePermission = (permId) => {
    setPermissions(prev => {
      if (prev.includes(permId)) {
        return prev.filter(p => p !== permId);
      } else {
        return [...prev, permId];
      }
    });
  };

  const selectAllPermissions = () => {
    setPermissions(ALL_PERMISSIONS.map(p => p.id));
  };

  const deselectAllPermissions = () => {
    setPermissions([]);
  };

  const savePermissions = async () => {
    if (!selectedEmp) return;
    try {
      setSaving(true);
      // Send permissions exactly as selected (allowing removal of core permissions if desired)
      await api.put(`/employees/${selectedEmp.emp_id}/permissions`, {
        permissions: permissions
      });
      addNotification("บันทึกสิทธิ์เรียบร้อยแล้ว", "success");
      setShowPermissionsModal(false);
      await loadEmployees();
    } catch (err) {
      addNotification(
        err.response?.data?.message || "บันทึกสิทธิ์ไม่สำเร็จ",
        "danger"
      );
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    if (!selectedEmp) return;
    const defaultPerms = ROLE_DEFAULTS[selectedEmp.login_role] || [];
    setPermissions(defaultPerms);
    addNotification("คืนค่าสิทธิ์เริ่มต้นเรียบร้อย (กดบันทึกเพื่อยืนยัน)", "info");
  };

  const removeLogin = async (emp) => {
    if (!isAdmin) return;
    if (
      !window.confirm(
        `ต้องการลบ account ล็อกอินของ "${emp.emp_name}" หรือไม่?`
      )
    )
      return;
    try {
      setSaving(true);
      await api.put(`/employees/${emp.emp_id}`, { username: "" });
      addNotification("ลบ account ล็อกอินสำเร็จ", "success");
      await loadEmployees();
    } catch (err) {
      addNotification(
        err.response?.data?.message || "ลบ account ล็อกอินไม่สำเร็จ",
        "danger"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dashboard-page employees-page container py-3">
      {/* Hero ด้านบน */}
      <div className="dashboard-hero mb-3">
        <div>
          <div className="dashboard-hero-title">
            <i className="bi bi-people-fill me-2"></i>จัดการพนักงาน (Employees)
          </div>
          <div className="dashboard-hero-subtitle">
            เพิ่ม แก้ไข และจัดการบัญชีล็อกอินของพนักงานในร้าน
          </div>
        </div>
        {isAdmin && (
          <button
            type="button"
            className="btn btn-ks-primary"
            style={{ color: '#ffffff' }}
            onClick={() => setShowAddModal(true)}
          >
            <i className="bi bi-person-plus me-1"></i>เพิ่มพนักงานใหม่
          </button>
        )}
      </div>

      {/* Modal เพิ่มพนักงานใหม่ */}
      {showAddModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form onSubmit={handleAdd}>
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-person-plus-fill me-2"></i>เพิ่มพนักงานใหม่
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowAddModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">ชื่อพนักงาน <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      name="emp_name"
                      value={form.emp_name}
                      onChange={handleFormChange}
                      placeholder="เช่น คุณกุ๊กกิ๊ก"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">ฐานเงินเดือน (บาท)</label>
                    <input
                      type="number"
                      className="form-control"
                      name="base_salary"
                      value={form.base_salary}
                      onChange={handleFormChange}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Username (ถ้าต้องการให้ล็อกอินได้)</label>
                    <input
                      type="text"
                      className="form-control"
                      name="username"
                      value={form.username}
                      onChange={handleFormChange}
                      placeholder="employee01"
                    />
                    <div className="form-text">
                      <i className="bi bi-info-circle me-1"></i>เว้นว่างไว้ได้ หากยังไม่ต้องการให้พนักงานล็อกอิน
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">สิทธิ์ (Role)</label>
                    <select
                      className="form-select"
                      name="role"
                      value={form.role}
                      onChange={handleFormChange}
                      disabled={!form.username}
                    >
                      <option value="employee">พนักงาน</option>
                      <option value="manager">ผู้จัดการ</option>
                      <option value="admin">แอดมิน</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowAddModal(false)}
                    disabled={saving}
                  >
                    ยกเลิก
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{ backgroundColor: "#D4AF37", borderColor: "#D4AF37" }}
                    disabled={saving}
                  >
                    <i className="bi bi-save me-1"></i>
                    {saving ? "กำลังบันทึก..." : "บันทึก"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Grid รายชื่อพนักงาน */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0 fw-bold text-secondary"><i className="bi bi-grid-fill me-2"></i>รายชื่อพนักงานทั้งหมด ({employees.length})</h5>
        {loading && <span style={{ minWidth: 120 }}><SkeletonLoader type="line" /></span>}
      </div>

      {loading ? (
        <div className="row g-3">
          <div className="col-md-6 col-lg-4"><SkeletonLoader type="card" /></div>
          <div className="col-md-6 col-lg-4"><SkeletonLoader type="card" /></div>
          <div className="col-md-6 col-lg-4"><SkeletonLoader type="card" /></div>
        </div>
      ) : employees.length === 0 && !loading ? (
        <div className="text-center text-muted py-5 bg-light rounded">ยังไม่มีข้อมูลพนักงาน</div>
      ) : (
        <div className="row g-3">
          {employees.map((emp) => (
            <div className="col-md-6 col-lg-4" key={emp.emp_id}>
              <div className={`card h-100 shadow-sm ${editId === emp.emp_id ? 'ring-2 ring-primary' : ''}`} style={{ border: '1px solid #D4AF37', background: 'linear-gradient(145deg, #ffffff 0%, #FFFDF5 100%)', boxShadow: '0 4px 15px rgba(212, 175, 55, 0.15)' }}>
                <div className="card-body">
                  {editId === emp.emp_id ? (
                    // Edit Mode
                    <div className="d-flex flex-column gap-2">
                      <div className="mb-2">
                        <label className="form-label form-label-sm text-muted">ชื่อ</label>
                        <input type="text" className="form-control form-control-sm" name="emp_name" value={editForm.emp_name} onChange={handleEditChange} />
                      </div>
                      <div className="mb-2">
                        <label className="form-label form-label-sm text-muted">เงินเดือน</label>
                        <input type="number" className="form-control form-control-sm" name="base_salary" value={editForm.base_salary} onChange={handleEditChange} />
                      </div>
                      <div className="row g-2 mb-2">
                        <div className="col-6">
                          <label className="form-label form-label-sm text-muted">Username</label>
                          <input type="text" className="form-control form-control-sm" name="username" value={editForm.username} onChange={handleEditChange} />
                        </div>
                        <div className="col-6">
                          <label className="form-label form-label-sm text-muted">Role</label>
                          <select className="form-select form-select-sm" name="role" value={editForm.role} onChange={handleEditChange} disabled={!editForm.username}>
                            <option value="employee">พนักงาน</option>
                            <option value="manager">ผู้จัดการ</option>
                            <option value="admin">แอดมิน</option>
                          </select>
                        </div>
                      </div>
                      <div className="d-flex gap-2 mt-2">
                        <button className="btn btn-primary btn-sm flex-grow-1" style={{ backgroundColor: "#D4AF37", borderColor: "#D4AF37" }} onClick={saveEdit} disabled={saving}>บันทึก</button>
                        <button className="btn btn-outline-secondary btn-sm flex-grow-1" onClick={cancelEdit}>ยกเลิก</button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <>
                      <div className="d-flex align-items-start mb-3">
                        <div className="rounded-circle p-3 me-3 d-flex align-items-center justify-content-center shadow-sm" style={{ width: 60, height: 60, background: 'linear-gradient(135deg, #D4AF37 0%, #AA8A2E 100%)', border: '2px solid #fff' }}>
                          <i className="bi bi-person-fill fs-2 text-white"></i>
                        </div>
                        <div className="flex-grow-1">
                          <h5 className="card-title mb-1 fw-bold text-dark">{emp.emp_name}</h5>
                          <div className="d-flex gap-1 flex-wrap">
                            {emp.login_username ? (
                              <>
                                <span className={`badge ${((emp.login_role || "employee") === "admin" ? "bg-danger" : (emp.login_role === "manager" ? "" : "bg-secondary bg-opacity-25 text-dark"))}`} style={emp.login_role === "manager" ? { backgroundColor: "#F4E4C1", color: "#8B7355" } : {}}>
                                  {(emp.login_role || "employee") === "admin" ? "Admin" : (emp.login_role === "manager" ? "Manager" : "Staff")}
                                </span>
                                {/* Debug: show raw role */}
                                <small className="text-muted ms-1" style={{ fontSize: '0.6rem' }}>({emp.login_role})</small>
                                <span className="badge bg-light text-dark border">
                                  <i className="bi bi-person-badge me-1"></i>{emp.login_username}
                                </span>
                              </>
                            ) : (
                              <span className="badge bg-secondary bg-opacity-25 text-secondary">No Login</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="mb-3 border-top pt-2">
                        <div className="d-flex justify-content-between text-muted small mb-1">
                          <span>ฐานเงินเดือน:</span>
                          <span className="fw-bold text-dark">
                            {emp.base_salary != null ? Number(emp.base_salary).toLocaleString("th-TH", { minimumFractionDigits: 2 }) : "-"} ฿
                          </span>
                        </div>
                      </div>

                      {isAdmin && (
                        <div className="d-flex gap-2 mt-auto">
                          <button className="btn btn-outline-primary btn-sm flex-grow-1" style={{ color: "#D4AF37", borderColor: "#D4AF37" }} onClick={() => startEdit(emp)} onMouseEnter={(e) => { e.target.style.backgroundColor = "#D4AF37"; e.target.style.color = "#fff"; }} onMouseLeave={(e) => { e.target.style.backgroundColor = "transparent"; e.target.style.color = "#D4AF37"; }}>
                            <i className="bi bi-pencil me-1"></i>แก้ไข
                          </button>
                          {emp.login_username && (
                            <div className="dropdown">
                              <button className="btn btn-outline-secondary btn-sm" type="button" data-bs-toggle="dropdown">
                                <i className="bi bi-three-dots-vertical"></i>
                              </button>
                              <ul className="dropdown-menu dropdown-menu-end">
                                <li><button className="dropdown-item" onClick={() => openPermissionsModal(emp)}><i className="bi bi-shield-lock me-2"></i>จัดการสิทธิ์</button></li>
                                <li><button className="dropdown-item" onClick={() => resetPassword(emp)}><i className="bi bi-key me-2"></i>รีเซ็ตรหัสผ่าน</button></li>
                                <li><hr className="dropdown-divider" /></li>
                                <li><button className="dropdown-item text-danger" onClick={() => removeLogin(emp)}><i className="bi bi-person-x me-2"></i>ลบ Account</button></li>
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal: Manage Permissions */}
      {showPermissionsModal && selectedEmp && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header bg-light">
                <div>
                  <h5 className="modal-title fw-bold d-flex align-items-center mb-1">
                    <i className="bi bi-shield-lock-fill text-primary me-2"></i>จัดการสิทธิ์: {selectedEmp.emp_name}
                  </h5>
                  <div className="small text-muted">
                    ตำแหน่ง: <span className="badge bg-secondary">{selectedEmp.login_role || 'employee'}</span>
                    <span className="ms-2">({permissions.length} จาก {ALL_PERMISSIONS.length} สิทธิ์ที่เปิด)</span>
                  </div>
                </div>
                <button type="button" className="btn-close" onClick={() => setShowPermissionsModal(false)}></button>
              </div>

              <div className="modal-body p-3" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="small text-muted">กำหนดสิทธิ์การเข้าถึงเมนูต่างๆ</span>
                  <div className="btn-group btn-group-sm">
                    <button type="button" className="btn btn-outline-secondary" onClick={resetToDefaults} title="คืนค่าตามบทบาทเริ่มต้น">
                      ค่าเริ่มต้น
                    </button>
                    <button type="button" className="btn btn-outline-secondary" onClick={selectAllPermissions}>
                      เปิดทั้งหมด
                    </button>
                    <button type="button" className="btn btn-outline-secondary" onClick={deselectAllPermissions}>
                      ปิดทั้งหมด
                    </button>
                  </div>
                </div>

                {/* Section 1: Core Features */}
                <div className="mb-3">
                  <div className="fw-bold small text-muted text-uppercase mb-2 px-1">
                    <i className="bi bi-star-fill text-warning me-1"></i> เมนูการทำงานหลัก (Core)
                  </div>
                  <div className="list-group shadow-sm">
                    {ALL_PERMISSIONS.filter(p => p.group === 'core').map(perm => (
                      <label key={perm.id} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-2" style={{ cursor: "pointer" }}>
                        <div className="d-flex align-items-center">
                          <i className={`bi ${perm.icon || 'bi-circle'} text-muted me-2 fs-6`}></i>
                          <span className="fw-medium">{perm.label}</span>
                          <span className="badge bg-secondary-subtle text-secondary border ms-2" style={{ fontSize: '0.65rem' }}>Core</span>
                        </div>
                        <div className="form-check form-switch mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={permissions.includes(perm.id)}
                            onChange={() => togglePermission(perm.id)}
                          />
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Section 2: Management & Finance */}
                <div>
                  <div className="fw-bold small text-muted text-uppercase mb-2 px-1">
                    <i className="bi bi-wallet2 text-success me-1"></i> เมนูการเงิน & การจัดการ (Management)
                  </div>
                  <div className="list-group shadow-sm">
                    {ALL_PERMISSIONS.filter(p => p.group === 'management').map(perm => {
                      const isCashflow = perm.id === 'cashflow';
                      return (
                        <label
                          key={perm.id}
                          className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center py-2 ${isCashflow ? 'border-primary bg-primary bg-opacity-10' : ''}`}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="me-2">
                            <div className="d-flex align-items-center">
                              <i className={`bi ${perm.icon || 'bi-circle'} ${isCashflow ? 'text-primary' : 'text-muted'} me-2 fs-6`}></i>
                              <span className={`fw-medium ${isCashflow ? 'text-primary fw-bold' : ''}`}>{perm.label}</span>
                              {perm.badge && (
                                <span className="badge bg-warning text-dark border ms-2" style={{ fontSize: '0.65rem' }}>
                                  <i className="bi bi-clock-history me-1"></i>{perm.badge}
                                </span>
                              )}
                            </div>
                            {perm.desc && (
                              <div className="text-muted small ps-4 mt-1" style={{ fontSize: '0.78rem' }}>
                                {perm.desc}
                              </div>
                            )}
                          </div>
                          <div className="form-check form-switch mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={permissions.includes(perm.id)}
                              onChange={() => togglePermission(perm.id)}
                            />
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="modal-footer justify-content-between bg-light">
                <button type="button" className="btn btn-outline-secondary" onClick={resetToDefaults}>
                  <i className="bi bi-arrow-counterclockwise me-1"></i>คืนค่าเริ่มต้น
                </button>
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowPermissionsModal(false)}>ปิด</button>
                  <button type="button" className="btn btn-primary" onClick={savePermissions} disabled={saving}>
                    {saving ? "กำลังบันทึก..." : "บันทึกสิทธิ์"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeesPage;
